import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, Clock, Repeat, Gift, Copy, Trophy, Loader2, Crown, Check, Cake, AlertTriangle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as DayCalendar } from "@/components/ui/calendar";
import Seo from "@/components/common/Seo";
import { Reveal } from "@/components/common/Reveal";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLE = { new: "bg-brand-red/10 text-brand-red", scheduled: "bg-amber-100 text-amber-700", done: "bg-green-100 text-green-700" };

const MEMBER_PERKS = [
  "10% off every detail, auto-applied at checkout",
  "Referral bonuses for you and your family",
  "50% off your birthday wash",
  "Priority scheduling — skip the queue",
  "Exclusive discounts at partner companies — cleaning, painting, deck staining, moving & more",
];

export default function Dashboard() {
  const { user, checkAuth } = useAuth();
  const [data, setData] = useState(null);
  const [joining, setJoining] = useState(false);
  const [dobInput, setDobInput] = useState("");
  const [savingDob, setSavingDob] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [partnerPerks, setPartnerPerks] = useState([]);

  const load = () => api.get("/me/dashboard").then(({ data }) => setData(data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (data && data.membership?.tier === "member") {
      api.get("/me/partner-perks").then(({ data }) => setPartnerPerks(data.perks || [])).catch(() => {});
    }
  }, [data]);

  const joinMembership = async () => {
    setJoining(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error("Could not start checkout. Please try again.");
      setJoining(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not start checkout. Please try again.";
      toast.error(msg);
      setJoining(false);
    }
  };

  const saveBirthday = async () => {
    setSavingDob(true);
    try {
      await api.post("/me/birthday", { dob: dobInput });
      await checkAuth();
      await load();
      toast.success("Birthday saved — your 50% birthday wash is unlocked!");
    } catch {
      toast.error("Could not save your birthday. Please try again.");
    } finally {
      setSavingDob(false);
    }
  };

  if (data === null) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-red" /></div>;
  if (data === false) return <div className="container-rv py-24 text-center text-brand-muted">Could not load your dashboard.</div>;

  const copyLink = () => { navigator.clipboard.writeText(data.referral.link); toast.success("Referral link copied!"); };
  const untilReward = data.loyalty.until_reward;
  const isMember = data.membership?.tier === "member";
  const memberFee = data.membership?.fee ?? 99;
  const membershipDob = data.membership?.dob;
  const birthdayActive = data.membership?.birthday_active;
  const birthdayMonth = membershipDob ? new Date(membershipDob + "T00:00:00").toLocaleString("en-US", { month: "long" }) : "";

  return (
    <div data-testid="dashboard-page" className="bg-brand-offwhite">
      <Seo title="My Dashboard | Reverence Autos" description="Manage your bookings, subscription and rewards." path="/dashboard" />
      <div className="container-rv py-16 lg:py-20">
        <Reveal>
          <p className="overline text-brand-red">My account</p>
          <h1 className="mt-3 font-serif text-4xl font-bold text-brand-charcoal sm:text-5xl">Welcome back, {user?.name?.split(" ")[0]}</h1>
        </Reveal>

        {/* stat cards */}
        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          <Reveal>
            <div className="card-rv h-full p-6">
              <div className="flex items-center gap-2 text-brand-red"><Trophy className="h-5 w-5" /><span className="overline">Loyalty</span></div>
              <p className="mt-4 font-serif text-3xl font-bold text-brand-charcoal">{data.loyalty.completed} details</p>
              <p className="mt-2 text-sm text-brand-muted" data-testid="loyalty-progress">
                {data.loyalty.reward_ready ? "🎉 Your next detail is 50% off!" : `${untilReward} ${untilReward === 1 ? "detail" : "details"} until your 50% off reward`}
              </p>
              <div className="mt-4 h-2 overflow-hidden rounded-full bg-brand-silver/30">
                <div className="h-full bg-brand-red" style={{ width: `${((6 - untilReward) / 6) * 100}%` }} />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="card-rv h-full p-6">
              <div className="flex items-center gap-2 text-brand-red"><Repeat className="h-5 w-5" /><span className="overline">Subscription</span></div>
              {data.subscription ? (
                <>
                  <p className="mt-4 font-serif text-2xl font-bold capitalize text-brand-charcoal">{data.subscription.plan}</p>
                  <p className="mt-2 text-sm text-brand-muted">Next visit: {data.subscription.next_visit || "—"}</p>
                </>
              ) : (
                <>
                  <p className="mt-4 text-sm text-brand-muted">No active subscription.</p>
                  <Link to="/pricing" className="mt-3 inline-block text-sm font-semibold text-brand-red hover:underline">Subscribe & save →</Link>
                </>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="card-rv h-full p-6" data-testid="dashboard-referral">
              <div className="flex items-center gap-2 text-brand-red"><Gift className="h-5 w-5" /><span className="overline">Referrals</span></div>
              <p className="mt-4 font-serif text-3xl font-bold text-brand-charcoal">${data.referral.credits.toFixed(0)} <span className="text-base font-normal text-brand-muted">credit</span></p>
              <p className="mt-1 text-sm text-brand-muted">{data.referral.count} friends referred</p>
              <button onClick={copyLink} data-testid="copy-referral-link" className="mt-4 flex w-full items-center justify-between gap-2 rounded-lg border border-brand-silver/40 bg-brand-offwhite px-3 py-2 text-xs text-brand-charcoal hover:border-brand-red">
                <span className="truncate">{data.referral.code}</span><Copy className="h-4 w-4 shrink-0" />
              </button>
            </div>
          </Reveal>
        </div>

        {/* Reverence Members */}
        <Reveal className="mt-8">
          {isMember ? (
            <div data-testid="member-status-card" className="rounded-2xl border border-brand-red/30 bg-gradient-to-r from-brand-charcoal to-[#2a2c2f] p-7 text-white">
              <div className="flex flex-wrap items-center justify-between gap-5">
                <div className="flex items-center gap-4">
                  <div className="grid h-12 w-12 place-items-center rounded-full bg-brand-red text-white"><Crown className="h-6 w-6" /></div>
                  <div>
                    <p className="flex items-center gap-2 font-serif text-xl font-bold">Reverence Member <Check className="h-4 w-4 text-brand-red" /></p>
                    <p className="text-sm text-brand-silver">Your perks are active. Enjoy the club.</p>
                  </div>
                </div>
                <Link to="/book" className="btn-red shrink-0" data-testid="member-book-btn">Book & save 10%</Link>
              </div>
              <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
                {MEMBER_PERKS.map((perk) => (
                  <li key={perk} className="flex items-start gap-2.5 text-sm text-brand-silver">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" /> {perk}
                  </li>
                ))}
              </ul>
              <div className="mt-6 border-t border-white/10 pt-5" data-testid="member-birthday-block">
                {membershipDob ? (
                  <p className="flex items-start gap-2.5 text-sm text-brand-silver">
                    <Cake className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" />
                    {birthdayActive ? (
                      <span data-testid="birthday-active-msg"><b className="text-white">Happy birthday month!</b> Your 50% birthday wash is active — book any detail this month and it's half price.</span>
                    ) : (
                      <span>Birthday saved. Your <b className="text-white">50% birthday wash</b> unlocks every {birthdayMonth}.</span>
                    )}
                  </p>
                ) : (
                  <div>
                    <p className="flex items-center gap-2 text-sm text-brand-silver"><Cake className="h-4 w-4 text-brand-red" /> Add your birthday to unlock your <b className="text-white">50% birthday wash</b>.</p>
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <Popover open={calOpen} onOpenChange={setCalOpen}>
                        <PopoverTrigger asChild>
                          <button type="button" data-testid="birthday-input" className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 px-4 py-2 text-sm text-white transition-colors hover:bg-white/10">
                            <Calendar className="h-4 w-4 text-brand-red" />
                            {dobInput ? new Date(dobInput + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "Pick your birthday"}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <DayCalendar
                            mode="single"
                            selected={dobInput ? new Date(dobInput + "T00:00:00") : undefined}
                            onSelect={(d) => {
                              if (d) {
                                const s = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                                setDobInput(s);
                                setCalOpen(false);
                              }
                            }}
                            captionLayout="dropdown-buttons"
                            fromYear={1940}
                            toYear={new Date().getFullYear()}
                            defaultMonth={dobInput ? new Date(dobInput + "T00:00:00") : new Date(1995, 0)}
                            disabled={{ after: new Date() }}
                          />
                        </PopoverContent>
                      </Popover>
                      <button onClick={saveBirthday} disabled={savingDob || !dobInput} className="btn-red disabled:opacity-40" data-testid="birthday-save-btn">
                        {savingDob ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save birthday"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {partnerPerks.length > 0 && (
                <div className="mt-6 border-t border-white/10 pt-5" data-testid="member-partner-perks">
                  <p className="text-xs font-semibold uppercase tracking-wider text-brand-silver">Partner perks — show your code</p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {partnerPerks.map((p) => (
                      <div key={p.code} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-white">{p.company}</p>
                          <p className="truncate text-xs text-brand-silver">{p.offer}</p>
                        </div>
                        <button onClick={() => copyCode(p.code)} data-testid={`partner-code-${p.code}`} className="shrink-0 rounded-md bg-brand-red px-2.5 py-1 text-xs font-semibold text-white transition-transform hover:scale-105">{p.code}</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div data-testid="member-join-card" className="rounded-2xl border border-brand-silver/40 bg-white p-7">
              <div className="flex flex-wrap items-center justify-between gap-5">
                <div className="flex items-center gap-4">
                  <div className="grid h-12 w-12 place-items-center rounded-full bg-brand-red/10 text-brand-red"><Crown className="h-6 w-6" /></div>
                  <div>
                    <p className="font-serif text-xl font-bold text-brand-charcoal">Become a Reverence Member</p>
                    <p className="max-w-md text-sm text-brand-muted">Just <b>${memberFee}/year</b> + HST (<b>$111.87</b> total) — secure checkout, membership activates instantly.</p>
                  </div>
                </div>
                <button onClick={joinMembership} disabled={joining} className="btn-red disabled:opacity-40 shrink-0" data-testid="member-join-btn">
                  {joining ? <Loader2 className="h-4 w-4 animate-spin" /> : "Join — $" + memberFee + "/yr + tax"}
                </button>
              </div>
              <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-brand-muted">Member perks</p>
              <ul className="mt-3 grid gap-2.5 sm:grid-cols-2">
                {MEMBER_PERKS.map((perk) => (
                  <li key={perk} className="flex items-start gap-2.5 text-sm text-brand-charcoal">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-red" /> {perk}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Reveal>

        {/* upcoming */}
        <Reveal className="mt-14">
          <h2 className="font-serif text-2xl font-bold text-brand-charcoal">Upcoming bookings</h2>
          <div className="mt-5 space-y-3">
            {data.upcoming.length === 0 && <p className="text-brand-muted">No upcoming bookings. <Link to="/book" className="font-semibold text-brand-red hover:underline">Book one now →</Link></p>}
            {data.upcoming.map((b) => <BookingCard key={b.id} b={b} />)}
          </div>
        </Reveal>

        {/* history */}
        {data.history.length > 0 && (
          <Reveal className="mt-12">
            <h2 className="font-serif text-2xl font-bold text-brand-charcoal">Booking history</h2>
            <div className="mt-5 space-y-3">{data.history.map((b) => <BookingCard key={b.id} b={b} showReport />)}</div>
          </Reveal>
        )}
      </div>
    </div>
  );
}

function ReportIssue({ b }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (msg.trim().length < 5) { toast.error("Please describe the issue (a few words)."); return; }
    setSending(true);
    try {
      await api.post("/me/complaints", { booking_id: b.id, message: msg.trim() });
      setDone(true);
      toast.success("Thanks — we've received your report and will make it right.");
      setTimeout(() => { setOpen(false); }, 1400);
    } catch {
      toast.error("Could not send your report. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setMsg(""); setDone(false); } }}>
      <button data-testid={`report-issue-btn-${b.id}`} onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full border border-brand-silver/50 px-3 py-1.5 text-xs font-medium text-brand-muted transition-colors hover:border-brand-red hover:text-brand-red">
        <AlertTriangle className="h-3.5 w-3.5" /> Report an issue
      </button>
      <DialogContent data-testid="report-issue-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Report an issue</DialogTitle>
          <DialogDescription>
            Not happy with your {b.pricing?.items?.[0]?.service || b.service} on {b.date}? Tell us what went wrong and we'll make it right.
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="report-issue-success">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-emerald-100 text-emerald-600"><Check className="h-6 w-6" /></div>
            <p className="font-medium text-brand-charcoal">Report received</p>
            <p className="text-sm text-brand-muted">We'll be in touch shortly to make this right.</p>
          </div>
        ) : (
          <>
            <Textarea data-testid="report-issue-textarea" value={msg} onChange={(e) => setMsg(e.target.value)} rows={4}
              placeholder="e.g. There were still crumbs under the seats and streaks on the windows…"
              className="resize-none focus-visible:ring-brand-red" />
            <DialogFooter className="gap-2 sm:gap-2">
              <DialogClose asChild>
                <button className="rounded-full border border-brand-silver/50 px-5 py-2.5 text-sm font-medium text-brand-charcoal hover:bg-brand-offwhite">Cancel</button>
              </DialogClose>
              <button data-testid="report-issue-submit" onClick={submit} disabled={sending} className="btn-red disabled:opacity-50">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit report"}
              </button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BookingCard({ b, showReport }) {
  return (
    <div data-testid={`booking-row-${b.id}`} className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-brand-silver/40 bg-white p-5">
      <div className="flex items-center gap-4">
        <div className="grid h-11 w-11 place-items-center rounded-full bg-brand-red/10 text-brand-red"><Calendar className="h-5 w-5" /></div>
        <div>
          <p className="font-semibold capitalize text-brand-charcoal">{b.pricing?.items?.[0]?.service || b.service} · {(b.vehicles || []).length} vehicle(s)</p>
          <p className="flex items-center gap-1 text-sm text-brand-muted"><Clock className="h-3.5 w-3.5" /> {b.date} · {b.time_slot}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        {showReport && <ReportIssue b={b} />}
        <span className="font-serif text-lg font-bold text-brand-charcoal">${b.pricing?.total?.toFixed(2)}</span>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${STATUS_STYLE[b.status] || ""}`}>{b.status}</span>
      </div>
    </div>
  );
}
